import React, { useEffect, useState } from 'react'
import { useAuth } from '~/routes/components/AuthProvider'
import { useDebounce } from 'use-lodash-debounce';
import { MonthInput, YearInput } from 'semantic-ui-calendar-react';
import FarmerGroupsService from '~/api/services/farmerGroups';
import { useTranslation } from 'react-i18next';
import { FarmersService } from '~/api/services/farmers/index';
import { Dropdown } from 'semantic-ui-react';
import dayjs from 'dayjs';
import { OPERATIVE_ROLE_SLUG, TRADING_VOLUME_ID } from '~/Constants';
import { formatNumber, handleErrors } from '~/utils/helpers';
import { TradActiFilterType, TradingActivitieSettingsType } from '~/api/services/reporting';
import { FilterWrapper } from './style';

interface FiltersProps {
  fetchFilteredData: (data: TradActiFilterType) => void
  checkedFilters: any[]
  dataFiltered: TradActiFilterType
  slug: string
  tradingSettings: TradingActivitieSettingsType
}

const defaultFilterValues = {
  yearFrom: '',
  yearTo: '',
  monthFrom: '',
  monthTo: '',
  farmerGroupId: '',
  farmerId: '',
  tradingVolumeTypeId: '',
  milkTypeId: '',
  customFilterId: ''
}

const Filters: React.FC<FiltersProps> = (props) => {
  const { checkedFilters, slug } = props
  const { t } = useTranslation()
  const [farmerGroupsOptions, setFarmerGroupOptions] = useState([])
  const { systemData: { operativeUsersCanSeeFarmersInfo, languages, logged: { languageId, roleSlug } } } = useAuth()
  const [loadingFarmerGroupOptions, setLoadingFarmerGroupOptions] = useState(false)
  const [dataFiltered, setDataFiltered] = useState(props.dataFiltered)
  const languageLocal = languages.find(language => language.id === languageId)
  const farmerFiltersDisabled = roleSlug === OPERATIVE_ROLE_SLUG && !operativeUsersCanSeeFarmersInfo
  const [searchQueryFarmer, setSearchQueryFarmer] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchValue = useDebounce(searchQuery, 300);
  const [loadingFarmersOptions, setLoadingFarmersOptions] = useState(false)
  const debouncedValueFarmer = useDebounce(searchQueryFarmer, 300)
  const [farmersOptions, setFarmersOptions] = useState([])

  useEffect(() => {
    if (JSON.stringify(dataFiltered) !== JSON.stringify(props.dataFiltered)) {
      setDataFiltered(props.dataFiltered)
    }
    if (!props.dataFiltered.farmerGroupId) {
      setSearchQuery('')
    }
    if (!props.dataFiltered.farmerId) {
      setSearchQueryFarmer('')
    }
  }, [props.dataFiltered])

  useEffect(() => {
    if (checkedFilters.length === 0) {
      setDataFiltered(defaultFilterValues)
    } else {
      Object.keys(dataFiltered).map(key => {
        if (!checkedFilters.find(filter => filter.key === key) && dataFiltered[key] !== '') {
          setDataFiltered({
            ...dataFiltered,
            [key]: ''
          })
          return
        }
      })
    }
  }, [checkedFilters.length])

  useEffect(() => {
    if (dataFiltered) {
      props.fetchFilteredData(dataFiltered)
    }
  }, [dataFiltered])

  useEffect(() => {
    handleSearchFarmerGroups(searchQuery)
  }, [debouncedSearchValue, slug]);

  useEffect(() => {
    handleSearchFarmers()
  }, [debouncedValueFarmer]);

  async function handleSearchFarmerGroups(searchQuery) {
    setLoadingFarmerGroupOptions(true)
    try {
      const response = await FarmerGroupsService.getFarmerGroupsList(slug, searchQuery)
      const options = response.data.map(item => ({ value: item.id, text: item.name }))
      setFarmerGroupOptions(options)
      setLoadingFarmerGroupOptions(false)
    } catch (error) {
      setLoadingFarmerGroupOptions(false)
      handleErrors(error)
    }
  }

  async function handleSearchFarmers() {
    setLoadingFarmersOptions(true)
    try {
      const response = await FarmersService.getFarmers({ farmerGroupId: dataFiltered.farmerGroupId, search: searchQueryFarmer, page: 0 })
      const options = response.data.map(item => ({ value: item.id, text: item.companyName }))
      setFarmersOptions(options)
      setLoadingFarmersOptions(false)
    } catch (error) {
      setLoadingFarmersOptions(false)
      setFarmersOptions([])
      handleErrors(error)
    }
  }

  async function handleChangeFarmerGroup(id) {
    setLoadingFarmersOptions(true)
    setDataFiltered({
      ...dataFiltered,
      farmerGroupId: id,
      farmerId: ''
    })
    setSearchQueryFarmer('')
    try {
      const responseFarmers = await FarmersService.getFarmers({ farmerGroupId: id, search: '', page: 0 })
      const optionsFarmers = responseFarmers.data.map(item => ({ value: item.id, text: item.companyName }))
      setFarmersOptions(optionsFarmers)
      setSearchQuery('')
    } catch (error) {
      handleErrors(error)
    }
    setLoadingFarmersOptions(false)
  }

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  function getNameOfMonthFromIndex(monthValue) {
    if (monthValue === '' || isNaN(monthValue)) return ''
    const date = new Date(2009, monthValue - 1, 10)
    const month = date.toLocaleString(dayjs.locale(), { month: 'long' });
    return month
  }

  function renderYearFilter(filter) {
    const currentYear = new Date().getFullYear()
    return <div style={{ display: 'flex', gap: 10, alignItems: 'center' }} key={Math.random()}>
      <YearInput
        key={Math.random()}
        name='date'
        autoComplete='off'
        placeholder={t(filter.placeholder)}
        closable
        clearIcon={'close'}
        minDate={'2004'}
        maxDate={(currentYear + 2).toString()}
        clearable
        onKeyPress={handleKeyPress}
        localization={languageLocal.code}
        closeOnMouseLeave={false}
        className={'datePicker'}
        value={dataFiltered[filter.key]}
        iconPosition='left'
        onChange={(event, { name, value }) => setDataFiltered({
          ...dataFiltered,
          [filter.key]: value
        })}
      />
      <MonthInput
        key={filter.monthKey}
        onKeyPress={handleKeyPress}
        name='date'
        autoComplete='off'
        placeholder={t(filter.monthPlaceholder)}
        closable
        clearIcon={'close'}
        clearable
        dateFormat={'MM'}
        localization={languageLocal.code}
        closeOnMouseLeave={false}
        className={'datePicker'}
        value={getNameOfMonthFromIndex(dataFiltered[filter.monthKey])}
        iconPosition='left'
        onChange={(event, data) => {
          setDataFiltered({
            ...dataFiltered,
            [filter.monthKey]: parseInt(data.value)
          })
        }}
      />
    </div>
  }

  function renderFarmerSelect() {
    return <Dropdown
      noResultsMessage={searchQuery !== '' ? t('No results found') : t('Please enter 1 or more character')}
      selection
      selectOnBlur={false}
      search={(options) => {
        return options
      }}
      clearable
      searchQuery={searchQueryFarmer}
      loading={loadingFarmersOptions}
      options={farmersOptions || []}
      placeholder={t('Search farmers')}
      value={dataFiltered.farmerId}
      id={'search-farmers-trading-activity'}
      onSearchChange={(e, { searchQuery }) => setSearchQueryFarmer(searchQuery)}
      onChange={(e, data) => {
        if (!data.value) {
          setSearchQueryFarmer('')
        } else {
          const item = farmersOptions.find(item => item.value === data.value)
          setSearchQueryFarmer(item.text)
        }
        setDataFiltered({
          ...dataFiltered,
          farmerId: data.value as string
        })
      }}
    />
  }

  function renderFarmerGroupSelect() {
    return <Dropdown
      noResultsMessage={searchQuery !== '' ? t('No results found') : t('Please enter 1 or more character')}
      selection
      selectOnBlur={false}
      search={(options) => {
        return options
      }}
      clearable
      searchQuery={searchQuery}
      loading={loadingFarmerGroupOptions}
      options={farmerGroupsOptions || []}
      id={'search-farmer-groups-trading-activity'}
      placeholder={t('Search farmer group')}
      value={dataFiltered.farmerGroupId}
      onSearchChange={(e, { searchQuery }) => setSearchQuery(searchQuery)}
      onChange={(e, data) => handleChangeFarmerGroup(data.value)}
    />
  }

  function renderDefaultSelect(filter) {
    let options

    if (filter.filterId === TRADING_VOLUME_ID) {
      options = props.tradingSettings?.[filter.optionsKey]?.map(volumeType =>
        ({ value: volumeType?.id, text: `${volumeType?.name} (${formatNumber(volumeType?.percent)} %)` })) || []
    } else {
      options = props.tradingSettings?.[filter.optionsKey]?.map(option => ({ value: option.id, text: option.name })) || []
    }

    return <Dropdown
      selection
      selectOnBlur={false}
      key={filter.filterId}
      clearable
      options={options || []}
      id={filter.filterId}
      placeholder={t(filter?.placeholder)}
      value={dataFiltered[filter.key]}
      onChange={(e, data) => setDataFiltered({
        ...dataFiltered,
        [filter.key]: data.value
      })}
    />
  }

  return <FilterWrapper>
    {renderFarmerGroupSelect()}
    {renderFarmerSelect()}
    <Dropdown
      selection
      selectOnBlur={false}
      clearable
      options={props.tradingSettings?.milkTypes.map(milkType => (
        {
          value: milkType?.milkTypeId,
          text: `${milkType?.name} (${milkType?.abbreviation})`
        }
      )) || []}
      id={'milkTypeId'}
      placeholder={t('Select type of milk')}
      value={dataFiltered.milkTypeId}
      onChange={(e, data) => setDataFiltered({
        ...dataFiltered,
        milkTypeId: data.value
      })}
    />
    {checkedFilters.map(filter => {
      switch (filter.type) {
        case 'date':
          return renderYearFilter(filter)
        case 'select':
          return !farmerFiltersDisabled && renderDefaultSelect(filter)
        default:
          return
      }
    })}
  </FilterWrapper>
}
export default Filters